import { useState } from "react";
import { withTranslation } from "next-i18next";
import { useRouter } from "next/router";
import queryString from "query-string";
import { useCurrentUser } from "@hooks/use-current-user";
import { useGiftCardInfos, useSetGiftCardInfos } from "@hooks/use-gift-card";
import { Button, Input } from "@components/core";
import { isStringNotEmpty, isEmailValid } from "@utils/validations";

function GiftCardForms({
  amount, standardGiftCard, t,
}) {
  const router = useRouter();
  const user = useCurrentUser();
  const giftCardInfos = useGiftCardInfos();
  const setGiftCardInfos = useSetGiftCardInfos();

  const [giftCard, setGiftCard] = useState({
    buyer_first_name: giftCardInfos?.buyer_first_name || user?.first_name || "",
    buyer_last_name: giftCardInfos?.buyer_last_name || user?.last_name || "",
    buyer_email: giftCardInfos?.buyer_email || user?.email || "",
    recipient_first_name: giftCardInfos?.recipient_first_name || "",
    recipient_last_name: giftCardInfos?.recipient_last_name || "",
    recipient_email: giftCardInfos?.recipient_email || "",
    send_email_to_recipient: giftCardInfos?.send_email_to_recipient || false,
  });
  const [errors, setErrors] = useState({});

  const fillData = (e) => {
    setGiftCard({
      ...giftCard,
      [e.target.name]: e.target.value,
    });
  };

  function setSendEmailToRecipient(value) {
    setGiftCard({
      ...giftCard,
      send_email_to_recipient: value,
    });
  }

  function goToPaymentPage(e) {
    e.preventDefault();

    const formErrors = {
      buyer_first_name: isStringNotEmpty(giftCard.buyer_first_name) ? "" : t("gift_cards:blank"),
      buyer_last_name: isStringNotEmpty(giftCard.buyer_last_name) ? "" : t("gift_cards:blank"),
      buyer_email: isEmailValid(giftCard.buyer_email) ? "" : t("gift_cards:email_invalid"),
    };

    if (standardGiftCard) {
      formErrors.recipient_first_name = isStringNotEmpty(giftCard.recipient_first_name) ? "" : t("gift_cards:blank");
      formErrors.recipient_last_name = isStringNotEmpty(giftCard.recipient_last_name) ? "": t("gift_cards:blank");
      formErrors.recipient_email = isEmailValid(giftCard.recipient_email) ? "" : t("gift_cards:email_invalid");
    }

    setErrors(formErrors);

    if (Object.values(formErrors).every((val) => !val)) {
      setGiftCardInfos(giftCard);

      const initQuery = `?amount=${amount}`;
      const giftCardInfosString = queryString.stringify(giftCard);
      const finalQuery = `${initQuery}&${giftCardInfosString}`;

      router.push(`checkout${finalQuery}`);
    }
  }

  return (
    <form
      className="mb-5 mx-4 mt-2 mt-md-4"
      onSubmit={goToPaymentPage}
    >
      <div className="gift-card-form-container mt-md-4">
        <div className="pt-3">
          <h1 className="h4 mb-3">
            {t("gift_cards:from")}
          </h1>
          <Input
            type="text"
            label={t("gift_cards:first_name")}
            name="buyer_first_name"
            placeholder={t("gift_cards:first_name")}
            className="gift-card-input"
            value={giftCard.buyer_first_name}
            onChange={(e) => fillData(e)}
            error={errors.buyer_first_name}
            isValidated={giftCard.buyer_first_name !== ""}
          />
          <Input
            type="text"
            label={t("gift_cards:last_name")}
            placeholder={t("gift_cards:last_name")}
            className="gift-card-input"
            name="buyer_last_name"
            value={giftCard.buyer_last_name}
            onChange={(e) => fillData(e)}
            error={errors.buyer_last_name}
            isValidated={giftCard.buyer_last_name !== ""}
          />
          <Input
            type="email"
            label={t("gift_cards:buyer_email")}
            placeholder={t("gift_cards:buyer_email")}
            className="gift-card-input"
            name="buyer_email"
            value={giftCard.buyer_email}
            onChange={(e) => fillData(e)}
            error={errors.buyer_email}
            isValidated={isEmailValid(giftCard.buyer_email)}
          />
        </div>
        {standardGiftCard && (
          <div className="pt-3">
            <h1 className="h4 mb-3">
              {t("gift_cards:to")}
            </h1>
            <Input
              type="text"
              label={t("gift_cards:first_name")}
              placeholder={t("gift_cards:first_name")}
              className="gift-card-input"
              name="recipient_first_name"
              value={giftCard.recipient_first_name}
              onChange={(e) => fillData(e)}
              error={errors.recipient_first_name}
              isValidated={giftCard.recipient_first_name !== ""}
            />
            <Input
              type="text"
              label={t("gift_cards:last_name")}
              placeholder={t("gift_cards:last_name")}
              className="gift-card-input"
              value={giftCard.recipient_last_name}
              onChange={(e) => fillData(e)}
              name="recipient_last_name"
              error={errors.recipient_last_name}
              isValidated={giftCard.recipient_last_name !== ""}
            />
            <Input
              type="email"
              label={t("gift_cards:recipient_email")}
              placeholder={t("gift_cards:recipient_email")}
              className="gift-card-input"
              name="recipient_email"
              value={giftCard.recipient_email}
              onChange={(e) => fillData(e)}
              error={errors.recipient_email}
              isValidated={isEmailValid(giftCard.recipient_email)}
            />
          </div>
        )}
      </div>
      {standardGiftCard && (
        <div
          className="gift-card-toogle-container ms-2 mx-md-auto mb-2 mt-md-4 d-flex flex-column justify-content-center"
        >
          <Input
            type="radio"
            label={t("gift_cards:receive_the_gift_card_by_email")}
            name="send_email_to_buyer"
            className="gift-card-toogle"
            checked={!giftCard.send_email_to_recipient}
            onClick={() => setSendEmailToRecipient(false)}
            readOnly
          />
          <Input
            type="radio"
            label={t("gift_cards:send_directly_the_gift_card_by_email")}
            name="send_email_to_recipient"
            className="gift-card-toogle"
            checked={giftCard.send_email_to_recipient}
            onClick={() => setSendEmailToRecipient(true)}
            readOnly
          />
        </div>
      )}

      <div className="gift-card-btn-container d-flex justify-content-center mx-auto pt-3">
        <Button
          className="mb-1"
          type="submit"
        >
          {t("gift_cards:proceed_to_payment")}
        </Button>
      </div>
    </form>
  );
}

export default withTranslation("gift_cards")(GiftCardForms);
